import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconTrash } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useManagementsList } from '../contexts/managements';
import { useStays, useCheckInStay, useMoveStay, useCloseStay, useDeleteStay } from '../contexts/stays';

import '../styles/billing.css';

const today = () => new Date().toLocaleDateString('en-CA');
const SOURCE_LABEL = { manual: 'Manual', import: 'Importado', move: 'Cambio' };

// Manejo (estadías) de una vaca (admin-billing-stays.md): la estadía abierta define el
// manejo actual y es la fuente de los días de pensión. Alta (check-in), cambio de
// manejo (move), salida (close), historial. Se monta en la vista de la donadora.
export default function StaysPanel({ animalId }) {
    const { data, isLoading, error } = useStays(animalId);
    const stays = data?.items || [];
    const openStay = stays.find((s) => s.open);

    const { data: mgmtData } = useManagementsList({ limit: 500, status: 2, sort: 'name' });
    const managements = mgmtData?.items || [];

    const { mutateAsync: checkIn, isPending: p1 } = useCheckInStay();
    const { mutateAsync: move, isPending: p2 } = useMoveStay();
    const { mutateAsync: close, isPending: p3 } = useCloseStay();
    const { mutateAsync: remove, isPending: p4 } = useDeleteStay();
    const busy = p1 || p2 || p3 || p4;

    const confirm = useConfirm();
    const [mode, setMode] = useState(null); // 'checkin' | 'move' | 'close'
    const [mgmtId, setMgmtId] = useState('');
    const [date, setDate] = useState(today());

    const start = (m) => { setMode(m); setMgmtId(''); setDate(today()); };
    const cancel = () => setMode(null);

    const submit = async () => {
        try {
            if (mode === 'checkin') {
                if (!mgmtId) { toast.error('Elegí el manejo'); return; }
                await checkIn({ animal_id: Number(animalId), management_id: Number(mgmtId), from_date: date });
                toast.success('Manejo registrado');
            } else if (mode === 'move') {
                if (!mgmtId) { toast.error('Elegí el nuevo manejo'); return; }
                await move({ animal_id: Number(animalId), new_management_id: Number(mgmtId), date });
                toast.success('Manejo cambiado');
            } else if (mode === 'close') {
                await close({ animal_id: Number(animalId), date });
                toast.success('Estadía cerrada');
            }
            setMode(null);
        } catch (er) {
            if (er?.status === 409) toast.error(er?.message || 'Conflicto: solape o sin estadía abierta');
            else if (er?.status === 422) toast.error(er?.message || 'Fecha inválida (rango vacío o anterior al inicio)');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo guardar');
        }
    };

    const del = async (s) => {
        const ok = await confirm({
            title: 'Eliminar estadía',
            message: '¿Eliminar esta estadía? Sale del cálculo de días.',
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await remove(s.id); toast.success('Estadía eliminada'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <section className="cat-detail__card">
            <div className="billing-stays__head">
                <h2 className="cat-detail__section-title">Manejo (estadías)</h2>
                {!openStay && mode !== 'checkin' && (
                    <button type="button" className="billing-btn billing-btn--primary billing-btn--sm" onClick={() => start('checkin')} disabled={busy}>Registrar manejo</button>
                )}
            </div>

            {error ? (
                <p className="billing-modal__error">{error.status === 403 ? 'Sin permiso para ver manejos (billing).' : 'No se pudieron cargar las estadías.'}</p>
            ) : isLoading ? (
                <p className="billing-modal__state">Cargando…</p>
            ) : (
                <>
                    {openStay ? (
                        <div className="billing-stays__current">
                            <span className="billing-stays__badge">En curso</span>
                            <span className="billing-stays__mgmt">{openStay.management?.name || '—'}</span>
                            <span className="billing-stays__since">desde {formatDateOnly(openStay.from_date)}</span>
                            {mode == null && (
                                <span className="billing-stays__actions">
                                    <button type="button" className="billing-btn billing-btn--ghost billing-btn--sm" onClick={() => start('move')} disabled={busy}>Cambiar manejo</button>
                                    <button type="button" className="billing-btn billing-btn--ghost billing-btn--sm" onClick={() => start('close')} disabled={busy}>Cerrar</button>
                                </span>
                            )}
                        </div>
                    ) : mode !== 'checkin' ? (
                        <p className="billing-stays__none">Sin manejo activo. Registrá el ingreso a un manejo.</p>
                    ) : null}

                    {mode && (
                        <div className="billing-add__row billing-stays__form">
                            {mode !== 'close' && (
                                <label className="billing-form__field">
                                    <span className="billing-form__label">{mode === 'move' ? 'Nuevo manejo' : 'Manejo'}</span>
                                    <select value={mgmtId} onChange={(e) => setMgmtId(e.target.value)} disabled={busy}>
                                        <option value="">Elegí…</option>
                                        {managements.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                                    </select>
                                </label>
                            )}
                            <label className="billing-form__field">
                                <span className="billing-form__label">{mode === 'checkin' ? 'Ingresa desde' : mode === 'move' ? 'Fecha del cambio' : 'Fecha de salida'}</span>
                                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                            </label>
                            <button type="button" className="billing-btn billing-btn--ghost" onClick={cancel} disabled={busy}>Cancelar</button>
                            <button type="button" className="billing-btn billing-btn--primary" onClick={submit} disabled={busy}>
                                {mode === 'checkin' ? 'Ingresar' : mode === 'move' ? 'Cambiar' : 'Cerrar'}
                            </button>
                        </div>
                    )}

                    {stays.length > 0 && (
                        <table className="cat-table billing-lines billing-stays__table">
                            <thead>
                                <tr><th>Manejo</th><th>Desde</th><th>Hasta</th><th>Origen</th><th aria-label="Acciones" /></tr>
                            </thead>
                            <tbody>
                                {stays.map((s) => (
                                    <tr key={s.id} className="cat-table__row">
                                        <td style={{ textAlign: 'left' }}>{s.management?.name || '—'}</td>
                                        <td>{formatDateOnly(s.from_date)}</td>
                                        <td>{s.to_date ? formatDateOnly(s.to_date) : <span className="billing-tag billing-tag--open">en curso</span>}</td>
                                        <td>{SOURCE_LABEL[s.source] || s.source || '—'}</td>
                                        <td><button type="button" className="billing-icon billing-icon--danger" onClick={() => del(s)} disabled={busy} aria-label="Eliminar" title="Eliminar"><IconTrash /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </section>
    );
}
